import { useMutation, useQueryClient } from '@tanstack/react-query';
import { assignSeatTwo } from '@/api/data-room-access.api';
import { dataRoomAccessKeys } from '@/hooks/data-room-access.queries';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { TEAM_STRINGS } from '@/lib/constants/team-strings';
import type { AssignSeatTwoBody } from '@/lib/types/data-room';

const COPY = TEAM_STRINGS.DATA_ROOM_SEATS;

/**
 * Gives seat 2 from a Team Access toggle (FR-139). A toggle has no surface
 * that stays open, so success and failure are both toasts (mnr-standards
 * "Write-action feedback"). The refreshed access view comes back in the
 * response and is written straight into the cache. A refusal refetches the
 * view: after a concurrent change the cached seat 2 holder is stale, and a
 * retry would only send the same out-of-date holder again.
 *
 * @param entityId - the startup entity
 */
export function useAssignSeatTwo(entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignSeatTwoBody) => assignSeatTwo(entityId, body),
    onSuccess: (access) => {
      queryClient.setQueryData(dataRoomAccessKeys.forStartup(entityId), access);
      toast.success(COPY.SAVED);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, COPY.FAILED));
      void queryClient.invalidateQueries({ queryKey: dataRoomAccessKeys.forStartup(entityId) });
    },
  });
}
